/**
 * Brief 4 · Gestor · Tela Qualidade IA — view presentacional (read-only).
 *
 * Dashboard de observabilidade de IA:
 *   - Comparação entre modelos (`GET /v1/ai-cost-monitoring/by-model`): tokens,
 *     invocações e latência P50/P95 por modelo de LLM.
 *   - Performance por agente (`GET /v1/quality/metrics/agents`).
 *   - **Consumo por conversa** (`GET /v1/ai-cost-monitoring/top-conversations` +
 *     `GET /v1/ai-cost-monitoring/conversations/:id/cost-by-agent`): ranking das
 *     conversas que mais consumiram + breakdown por agente ao clicar.
 *
 * ⚠️ Esta tela NÃO mostra valor em dinheiro desde 17/Set/2026. O que ela mostrava
 * era `costEstimateBrl`: o que a PLATAFORMA paga ao fornecedor de IA — número da
 * NetComet, não do tenant, exibido ao gestor do cliente. Auditoria de escopo
 * plataforma × tenant, achado B-1. Valor pelo contrato de preço do tenant é outra
 * grandeza e chega na Fatia B. As URLs mantêm os nomes antigos (trocá-las é
 * quebra de contrato).
 *
 * Props `null` = sessão sem a capability `ai-usage-read`
 * (nota honesta no lugar do painel). Banners spike/degradado/fallback do gabarito
 * seguem sem fonte → não renderizados.
 */
import type {
  AiModelStatRaw,
  ConversationAgentCost,
  TopConversationCost,
} from '../supervisor/types';
import type { AgentRowVM } from '../supervisor/view-models';
import type React from 'react';

// B-1 (17/Set/2026): o formatador de R$ saiu junto com o campo. O que esta tela
// formata agora é contagem — tokens e invocações — em pt-BR.
function num(value: number): string {
  return value.toLocaleString('pt-BR');
}

function secs(lat: number): string {
  return `${lat.toFixed(1).replace('.', ',')}s`;
}

/** Latência em ms vinda do backend; `—` quando nenhuma invocação registrou latência. */
function ms(value: number | null): string {
  return value === null ? '—' : `${value.toLocaleString('pt-BR')} ms`;
}

/** Subset do response para o painel de breakdown por agente. */
export interface SelectedConversationCost {
  conversationId: string;
  results: ConversationAgentCost[];
  totalTokens: number;
}

export interface QualidadeIaViewProps {
  agents: AgentRowVM[];
  /** Comparação por modelo; `null` quando a sessão não tem `ai-usage-read`. */
  modelStats: AiModelStatRaw[] | null;
  /**
   * Top conversas por consumo; `null` quando a sessão não tem `ai-usage-read`
   * (seção inteira fica oculta). `undefined` = prop omitida (backward-compat).
   */
  topConversations?: TopConversationCost[] | null;
  /**
   * Breakdown por agente da conversa selecionada; `null` quando nenhuma conversa
   * está selecionada ou o fetch ainda não retornou.
   */
  selectedConversationCost?: SelectedConversationCost | null;
  /** Callback disparado ao clicar numa linha do ranking de conversas. */
  onSelectConversation?: (conversationId: string) => void;
}

export function QualidadeIaView({
  agents,
  modelStats,
  topConversations,
  selectedConversationCost,
  onSelectConversation,
}: QualidadeIaViewProps): React.JSX.Element {
  const totalInv = agents.reduce((a, r) => a + r.inv, 0);

  return (
    <div className="qualidade-ia-screen">
      <header className="qualidade-ia-screen__head">
        <div className="qualidade-ia-screen__crumb">Estratégia</div>
        <h1 className="qualidade-ia-screen__title">Gestão de Qualidade IA</h1>
        <p className="qualidade-ia-screen__sub">
          Observabilidade de consumo e performance dos agentes de IA do tenant.
        </p>
        <p className="qualidade-ia-screen__note">
          Esta tela mostra <b>consumo</b>: tokens e invocações por agente e modelo. Valor por
          conversa chega quando o contrato de preço do tenant estiver ligado.
        </p>
      </header>

      <section className="qia-panel">
        <h2 className="qia-panel__title">Comparação entre modelos</h2>
        {modelStats === null ? (
          <div className="qia-defer" role="status">
            <strong>Comparação entre modelos indisponível.</strong> Requer a capability{' '}
            <code>ai-usage-read</code> (Pro+) na sua sessão.
          </div>
        ) : modelStats.length === 0 ? (
          <p className="qia-empty">Sem invocações de IA no período para comparar modelos.</p>
        ) : (
          <table className="qia-table">
            <thead>
              <tr>
                <th scope="col">Modelo</th>
                <th scope="col" className="qia-num">
                  Invocações
                </th>
                <th scope="col" className="qia-num">
                  Tokens
                </th>
                <th scope="col" className="qia-num">
                  Latência P50
                </th>
                <th scope="col" className="qia-num">
                  Latência P95
                </th>
              </tr>
            </thead>
            <tbody>
              {modelStats.map((m) => (
                <tr key={m.modelUsed} className="qia-row">
                  <td className="qia-row__name">
                    <span className="qia-row__label">{m.modelUsed}</span>
                  </td>
                  <td className="qia-num">{m.invocations.toLocaleString('pt-BR')}</td>
                  <td className="qia-num qia-mono">{num(m.totalTokens)}</td>
                  <td className="qia-num qia-mono">{ms(m.latencyP50Ms)}</td>
                  <td className="qia-num qia-mono">{ms(m.latencyP95Ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="qia-panel">
        <h2 className="qia-panel__title">Performance por agente</h2>

        {agents.length === 0 ? (
          <p className="qia-empty">
            Sem métricas de IA no período. As agregações são materializadas diariamente.
          </p>
        ) : (
          <table className="qia-table">
            <thead>
              <tr>
                <th scope="col">Agente</th>
                <th scope="col" className="qia-num">
                  Invocações
                </th>
                <th scope="col" className="qia-num">
                  Tokens
                </th>
                <th scope="col" className="qia-num">
                  Latência
                </th>
                <th scope="col" className="qia-num">
                  Aceitação
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className="qia-row">
                  <td className="qia-row__name">
                    <span className="qia-row__label">{a.label}</span>
                    <span className="qia-row__id">{a.id}</span>
                    {a.id === 'tool-agent' && (
                      <span className="qia-row__note">· compositor (consultado por outros)</span>
                    )}
                  </td>
                  <td className="qia-num">{a.inv.toLocaleString('pt-BR')}</td>
                  <td className="qia-num qia-mono">{a.tokens}</td>
                  <td className="qia-num qia-mono">{secs(a.lat)}</td>
                  <td className="qia-num">
                    {a.accept != null ? <b>{a.accept}%</b> : <span className="qia-na">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="qia-row qia-row--total">
                <td className="qia-row__name">Total</td>
                <td className="qia-num">{num(totalInv)}</td>
                <td className="qia-num" />
                <td className="qia-num" />
                <td className="qia-num" />
              </tr>
            </tfoot>
          </table>
        )}
      </section>

      {/* Seção "Consumo por conversa" — só renderiza quando a cap está presente
          (topConversations !== null e !== undefined). topConversations=null → sem cap. */}
      {topConversations != null && (
        <section className="qia-panel">
          <h2 className="qia-panel__title">Consumo por conversa</h2>

          {topConversations.length === 0 ? (
            <p className="qia-empty">Nenhuma conversa com consumo de IA no período.</p>
          ) : (
            <>
              <table className="qia-table">
                <thead>
                  <tr>
                    <th scope="col">Conversa</th>
                    <th scope="col" className="qia-num">
                      Tokens
                    </th>
                    <th scope="col" className="qia-num">
                      Invocações
                    </th>
                    <th scope="col" className="qia-num">
                      Agentes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topConversations.map((c) => (
                    <tr
                      key={c.conversationId}
                      className="qia-row qia-row--selectable"
                      onClick={() => onSelectConversation?.(c.conversationId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="qia-row__name">
                        <span className="qia-row__id">{c.conversationId}</span>
                      </td>
                      <td className="qia-num qia-mono">{num(c.totalTokens)}</td>
                      <td className="qia-num">{c.logicalInvocations}</td>
                      <td className="qia-num">{c.agentCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {selectedConversationCost != null && (
                <div className="qia-breakdown">
                  <h3 className="qia-breakdown__title">
                    Breakdown por agente —{' '}
                    <span className="qia-row__id">{selectedConversationCost.conversationId}</span>
                  </h3>
                  <table className="qia-table">
                    <thead>
                      <tr>
                        <th scope="col">Agente</th>
                        <th scope="col" className="qia-num">
                          Invocações
                        </th>
                        <th scope="col" className="qia-num">
                          Tokens
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedConversationCost.results.map((a) => (
                        <tr key={a.agentId} className="qia-row">
                          <td className="qia-row__name">
                            <span className="qia-row__id">{a.agentId}</span>
                          </td>
                          <td className="qia-num">{a.logicalInvocations}</td>
                          <td className="qia-num qia-mono">{num(a.totalTokens)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="qia-row qia-row--total">
                        <td className="qia-row__name">Total</td>
                        <td className="qia-num" />
                        <td className="qia-num qia-mono">
                          {num(selectedConversationCost.totalTokens)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <p className="qia-foot">
        Agentes nominais de IA do tenant. O agente de ferramentas (tool-agent) é compositor —
        consultado por outros agentes, não atua como agente nominal próprio.
      </p>
    </div>
  );
}
